const normalizeId = (value) => `${value ?? ""}`;

const normalizeScheduleStructureRows = (day_structure = {}) => {
  if (Array.isArray(day_structure)) {
    return day_structure.map((row, index) => ({
      schedule_period_id: row?.schedule_period_id ?? row?.period_id ?? index,
      course_period_id: row?.course_period_id ?? (typeof row === "object" ? null : row),
    }));
  }

  return Object.entries(day_structure || {}).map(([schedule_period_id, row]) => ({
    schedule_period_id: row?.schedule_period_id ?? row?.period_id ?? schedule_period_id,
    course_period_id: row?.course_period_id ?? (typeof row === "object" ? null : row),
  }));
};

const quarterDaysOverlap = (left_quarter_days = [], right_quarter_days = []) => {
  const left_keys = new Set(
    (Array.isArray(left_quarter_days) ? left_quarter_days : []).map(
      (quarter_day) => `${quarter_day?.day ?? ""}:${quarter_day?.quarter ?? ""}`
    )
  );

  return (Array.isArray(right_quarter_days) ? right_quarter_days : []).some(
    (quarter_day) => left_keys.has(`${quarter_day?.day ?? ""}:${quarter_day?.quarter ?? ""}`)
  );
};

export const buildClassroomAvailabilityContext = ({
  sectionInfo = {},
  sections = [],
  scheduleInfo = {},
  scheduleStructureData = {},
}) => {
  const excluded_section_ids = new Set();
  const potential_course_period_quarter_days = [];
  const sections_by_id = new Map(
    sections.map((section) => [normalizeId(section?.section_id), section])
  );

  const addSectionToAvailabilityFamily = (section = {}) => {
    if (!section.section_id) return;

    excluded_section_ids.add(normalizeId(section.section_id));
    (Array.isArray(section.course_periods) ? section.course_periods : []).forEach(
      (course_period_id) => {
        if (!course_period_id) return;
        potential_course_period_quarter_days.push({
          course_period_id: normalizeId(course_period_id),
          quarter_days: Array.isArray(section.quarter_days) ? section.quarter_days : [],
        });
      }
    );

    (Array.isArray(section.subsections) ? section.subsections : []).forEach(
      (subsection_id) => {
        const subsection_info = sections_by_id.get(normalizeId(subsection_id));
        if (!subsection_info || `${subsection_info.has_separate_classroom || "0"}` === "1") {
          return;
        }

        excluded_section_ids.add(normalizeId(subsection_info.section_id));
        (Array.isArray(subsection_info.course_periods)
          ? subsection_info.course_periods
          : []
        ).forEach((course_period_id) => {
          if (!course_period_id) return;
          potential_course_period_quarter_days.push({
            course_period_id: normalizeId(course_period_id),
            quarter_days: Array.isArray(subsection_info.quarter_days)
              ? subsection_info.quarter_days
              : [],
          });
        });
      }
    );
  };

  addSectionToAvailabilityFamily(sectionInfo);

  if (sectionInfo.span_id) {
    sections.forEach((section) => {
      if (
        normalizeId(section?.span_id) !== normalizeId(sectionInfo.span_id) ||
        normalizeId(section?.section_id) === normalizeId(sectionInfo.section_id)
      ) {
        return;
      }

      addSectionToAvailabilityFamily(section);
    });
  }

  if (scheduleInfo.schedule_type === "daily") {
    const schedule_structure = scheduleStructureData.schedule_structure || {};
    const availability_matrix = scheduleStructureData.availability_matrix || {};
    const course_periods = scheduleStructureData.course_periods || {};
    const conflicting_potentials = [];

    potential_course_period_quarter_days.forEach((potential) => {
      const course_period_info = course_periods[potential.course_period_id] || {};
      const schedule_period_ids = Object.values(course_period_info.days || {}).filter(Boolean);

      schedule_period_ids.forEach((schedule_period_id) => {
        const conflicting_schedule_period_ids = new Set(
          (availability_matrix[schedule_period_id] || []).map(normalizeId)
        );

        Object.values(schedule_structure).forEach((day_structure) => {
          normalizeScheduleStructureRows(day_structure).forEach((row) => {
            if (
              !row.course_period_id ||
              !conflicting_schedule_period_ids.has(normalizeId(row.schedule_period_id))
            ) {
              return;
            }

            conflicting_potentials.push({
              course_period_id: normalizeId(row.course_period_id),
              quarter_days: potential.quarter_days,
            });
          });
        });
      });
    });

    potential_course_period_quarter_days.push(...conflicting_potentials);
  }

  return {
    excludedSectionIds: excluded_section_ids,
    potentialCoursePeriodQuarterDays: potential_course_period_quarter_days,
  };
};

export const isClassroomAvailableForSection = ({
  classroomInfo,
  sections = [],
  availabilityContext = {},
}) => {
  if (!classroomInfo?.classroom_id) return false;
  if (`${classroomInfo.multi_class || "0"}` === "1") return true;

  const classroom_id = normalizeId(classroomInfo.classroom_id);
  const excluded_section_ids = availabilityContext.excludedSectionIds || new Set();
  const potentials = availabilityContext.potentialCoursePeriodQuarterDays || [];

  return !sections.some((section) => {
    if (normalizeId(section?.classroom_id) !== classroom_id) return false;
    if (excluded_section_ids.has(normalizeId(section?.section_id))) return false;

    const section_course_period_ids = new Set(
      (Array.isArray(section?.course_periods) ? section.course_periods : [])
        .filter(Boolean)
        .map(normalizeId)
    );
    if (section_course_period_ids.size === 0) return false;

    return potentials.some(
      (potential) =>
        section_course_period_ids.has(normalizeId(potential.course_period_id)) &&
        quarterDaysOverlap(potential.quarter_days, section.quarter_days)
    );
  });
};
